import { DatabaseApplicationService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DatabaseApplication, DBToAppBulkDeleteParams, DBToAppBulkUpdateParams, SchemaProperty } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, Query } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { deepCopy, generatePluralStr } from '../../common/util';
import DependencyValidationBox, { ResourceTypes } from '../data-quality/DependencyValidationBox';
import PortfolioTableActionGroup from './common/PortfolioTableActionGroup';
import { APPLICATION_ID_COLUMN, ColumnOrderType, EXCLUDED_DB_APP_COLUMNS } from './constants';
import { InlineEditContext, InlineEditRowItem, InlineEditValue } from './inline-editing/InlineEditContext';
import { SchemaPropertyMap } from './types';
import { generateColumnDefs } from './utils';

type DBToAppProps = FlashContextInterface;

const dbAppService = new DatabaseApplicationService();
const schemaService = new SchemaService();
const resourceName = 'database-applications';

const DBToAppPage: FunctionComponent<DBToAppProps> = ({flash}) => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const portfolioId = match?.params?.portfolioId;
    const isMounted = useIsMounted();
    const [ items, setItems ] = useState<DatabaseApplication[]>([]);
    const [ schemas, setSchemas ] = useState<SchemaProperty[]>([]);
    const [ schemaMap, setSchemaMap ] = useState<SchemaPropertyMap>({});
    const [ columnDef, setColumnDef ] = useState<ColumnDefinition<DatabaseApplication>[]>([]);
    const [ selectedItems, setSelectedItems ] = useState<DatabaseApplication[]>([]);
    const [ filteredItems, setFilteredItems ] = useState<DatabaseApplication[] | null>(null);
    const [ loading, setLoading ] = useState(true);
    const [ defaultQuery, setDefaultQuery ] = useState<Query>();
    const { updateInlineEditFn } = useContext(InlineEditContext);

    const loadData = async () => {
        setLoading(true);
        setSelectedItems([]);
        try {
            if (!portfolioId) {
                flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
                return;
            }
            // Load data
            const dbToAppMappings = await dbAppService.list(portfolioId);
            if (!isMounted()) {
                return;
            }
            dbToAppMappings.forEach(dbApp => {
                // TODO (tech debt): Fix the inconsistency naming between dbToAppMappings and appToServerMappings
                // https://issues.amazon.com/issues/PAT-27216
                dbApp.applicationMapped = dbApp.applicationMappings?.inScope ? constants.MAPPED: constants.UNMAPPED;
                dbApp.databaseMapped = dbApp.databaseMappings?.inScope ? constants.MAPPED: constants.UNMAPPED;
            });
            setItems(dbToAppMappings);

            // Load schema
            const schemaList = await schemaService.listAttributes(resourceName, portfolioId);
            if (!isMounted()) {
                return;
            }
            const mappedFields: SchemaProperty[] = [{
                value: 'applicationMapped',
                text: constants.APPLICATION_MAPPED,
                messages: {
                    updateForbidden: constants.THIS_FIELD_CANNOT_BE_UPDATED,
                },
            }, {
                value: 'databaseMapped',
                text: constants.DATABASE_MAPPED,
                messages: {
                    updateForbidden: constants.THIS_FIELD_CANNOT_BE_UPDATED,
                },
            }];
            const newSchemaList = [...schemaList, ...mappedFields];
            setSchemas(newSchemaList);
            const dbAppHeaders: SchemaPropertyMap = {};
            newSchemaList.forEach(schema => {
                dbAppHeaders[schema.value] = schema;
            });
            setSchemaMap(dbAppHeaders);

            // Build column definitions
            const columnDefinitions = generateColumnDefs(dbToAppMappings, dbAppHeaders, ColumnOrderType.DATABASE_APPLICATION, EXCLUDED_DB_APP_COLUMNS, true);
            setColumnDef(columnDefinitions);
        } catch (err) {
            if (!isMounted()) {
                return;
            }
            flash.alert(FlashType.error, (err as Error).message, constants.DB_APP_ERROR_LOADING);
            setItems([]);
            setColumnDef([]);
        } finally {
            if (!isMounted()) {
                return;
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioId]);

    const inlineEditFunction = async (rowItem: InlineEditRowItem, columnId: string, updatedValue: InlineEditValue) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }

        const conditions: DBToAppBulkUpdateParams = {
            fieldName: columnId,
            fieldValue: updatedValue,
            portfolioId,
            databaseApplicationIds: [{
                applicationId: (rowItem as DatabaseApplication).applicationId,
                databaseId: (rowItem as DatabaseApplication).databaseId,
                portfolioId,
            }],
        };

        try {
            await dbAppService.update(conditions);
            const updateSuccessMsg = generatePluralStr(1, constants.DB_APP_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DB_APP_EDITING_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    useEffect(() => {
        updateInlineEditFn(inlineEditFunction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Delete multiple items. If items are selected, they will be deleted. If there are no selected items,
     * then all filtered items will be deleted.
     * @returns void
     */
    const onConfirmBulkDeletion = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        try {
            const selectedIds = (selectedItems.length || !filteredItems ? selectedItems : filteredItems).map(selectedItem => ({
                applicationId: selectedItem.applicationId,
                portfolioId,
                databaseId: selectedItem.databaseId,
            }));
            const conditions: DBToAppBulkDeleteParams = {
                portfolioId,
                databaseApplicationIds: selectedIds,
            };
            await dbAppService.deleteMappings(conditions); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DB_APP_DELETE_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const onConfirmDeleteAll = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        try {
            await dbAppService.clear(portfolioId); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DB_APP_DELETE_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const onConfirmBulkEdit = async (
        selectAll: boolean,
        selectedHeader: string,
        updatedValue: string | number | boolean,
    ) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        const selectedSchema = schemaMap[selectedHeader];
        if (!selectedHeader || !selectedSchema) {
            flash.alert(FlashType.error, constants.BULK_UPDATE_ERROR_CONTENT, constants.DB_APP_EDITING_ERROR_HEADING);
            return;
        }
        const itemsToUpdate = selectAll ? (filteredItems ?? items) : selectedItems;
        const selectedIds = itemsToUpdate.map(item => ({
            applicationId: item.applicationId,
            databaseId: item.databaseId,
            portfolioId,
        }));
        
        const conditions: DBToAppBulkUpdateParams = {
            fieldName: selectedHeader,
            fieldValue: updatedValue,
            portfolioId,
            databaseApplicationIds: selectedIds,
        };

        try {
            await dbAppService.update(conditions);
            const updateSuccessMsg = generatePluralStr(selectedIds.length || items.length, constants.DB_APP_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DB_APP_EDITING_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const defaultSortingState: SortingState<DatabaseApplication> = {
        isDescending: false,
        sortingColumn: {
            sortingField: APPLICATION_ID_COLUMN,
        },
    };

    const onMappingFilterChange = (filterBy: string) => {
        setDefaultQuery({
            tokens: [{
                value: constants.UNMAPPED,
                propertyKey: filterBy,
                operator: '='
            }],
            operation: 'and',
        });
    };

    const filterChanged = (items: readonly DatabaseApplication[] | null) => {
        setFilteredItems(deepCopy(items));
    };

    return (
        <SpaceBetween size="m">
            <DependencyValidationBox
                dependencyType={ResourceTypes.APPLICATION_DATABASE}
                dependencyItems={items}
                onFilter={onMappingFilterChange} />
            <AWSCATTable
                resourceName={constants.DB_APP_TABLE_HEADER}
                resourceType='databaseApplication'
                items={items}
                importWhenEmpty={true}
                columnDefinitions={columnDef}
                loading={loading}
                resizeable={false} // explicitly not allowing resize since there are few columns and all get crammed to the left
                autoGenerateVisibleContent={true}
                onSelectionChange={setSelectedItems as () => void}
                onFilterChange={filterChanged}
                headerActions={
                    <PortfolioTableActionGroup
                        resourceType='databaseApplication'
                        resourceName={constants.DB_APP_RESOURCE_NAME}
                        resourceCount={items.length}
                        selectionCount={selectedItems.length}
                        hasFilter={!!filteredItems}
                        filteredCount={filteredItems?.length ?? 0}
                        loading={loading}
                        onConfirmDeletion={onConfirmBulkDeletion}
                        onConfirmDeleteAll={onConfirmDeleteAll}
                        onConfirmBulkEdit={onConfirmBulkEdit}
                        schemaProperties={schemas}
                        schemaPropertyMap={schemaMap}
                    />
                }
                selectionType='multi'
                defaultSortBy={defaultSortingState}
                useDefaultDownload
                defaultQuery={defaultQuery}
            />
        </SpaceBetween>
    );
};

export default withFlashContext(DBToAppPage);
